import React, { useState } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import CheckIcon from "@mui/icons-material/Check";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTheme } from "@mui/material/styles";
import { FeatureRequest } from "../../types/featureRequest";
import { createFeatureRequestBannerStyles } from "./styles";

/**
 * Props for the FeatureRequestCard component
 */
interface FeatureRequestCardProps {
  /** The feature request data to display */
  feature: FeatureRequest;
  /** Callback function to handle voting for a feature */
  onVote: (featureId: string, userId: string) => void;
  /** Function to check if the current user has voted for a feature */
  hasUserVoted: (featureId: string) => boolean;
  /** The current user's ID */
  userId: string;
}

/**
 * FeatureRequestCard Component
 *
 * Displays an individual feature request with expandable description and voting functionality.
 * Features a collapsible design where the description is hidden by default and can be expanded
 * by clicking on the card. Vote button uses theme.seaweed background with white icon.
 */
const FeatureRequestCard: React.FC<FeatureRequestCardProps> = ({
  feature,
  onVote,
  hasUserVoted,
  userId,
}) => {
  const theme = useTheme();
  const featureRequestBannerStyles = createFeatureRequestBannerStyles(theme);
  const [isExpanded, setIsExpanded] = useState(false);

  /**
   * Handles voting for the feature request
   */
  const handleVote = () => {
    onVote(feature._id, userId);
  };

  /**
   * Toggles the expanded state of the card to show/hide description
   */
  const handleToggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const userHasVoted = hasUserVoted(feature._id);

  return (
    <Box sx={featureRequestBannerStyles.card}>
      {/* Card header */}
      <Box
        sx={featureRequestBannerStyles.cardHeader}
        onClick={handleToggleExpanded}
      >
        {/* Title section */}
        <Box sx={featureRequestBannerStyles.cardTitleSection}>
          <ExpandMoreIcon
            sx={featureRequestBannerStyles.cardExpandIcon(isExpanded)}
          />
          <Typography
            variant="body2"
            sx={featureRequestBannerStyles.cardTitleText}
          >
            {feature.title}
          </Typography>
        </Box>
        {/* Vote section */}
        <Box sx={featureRequestBannerStyles.cardVoteSection}>
          <Typography
            variant="caption"
            sx={featureRequestBannerStyles.cardVoteCount}
          >
            {feature.votes} vote{feature.votes !== 1 ? "s" : ""}
          </Typography>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              handleVote();
            }}
            size="small"
            sx={featureRequestBannerStyles.cardVoteButton(userHasVoted)}
          >
            {userHasVoted ? (
              <CheckIcon fontSize="small" />
            ) : (
              <ThumbUpOutlinedIcon fontSize="small" />
            )}
          </IconButton>
        </Box>
      </Box>
      {/* Description section */}
      <Box sx={featureRequestBannerStyles.cardDescriptionContainer(isExpanded)}>
        {/* Description */}
        <Box sx={featureRequestBannerStyles.cardDescription}>
          <Typography
            variant="caption"
            sx={featureRequestBannerStyles.cardDescriptionText}
          >
            {feature.description}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default FeatureRequestCard;
